import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { FLEET_HAND_BACK_CHECK_TTL_MS, useClaudeFleetService } from '@/services/claude-fleet'
import type { FleetHandBackCheck, FleetHandBackMode, FleetRunHandBackResult } from '@/services/claude-fleet'
import { fleetErrorText } from '@/stores/claude-fleet.store'

/**
 * The fleet client shows no toast of its own on a 403, and a 403 without the fleet's `error` text reads as '', so the
 * caller's own words stand in for it: the menu and the toast are the only places the person learns it was refused.
 */
function refusalText(failure: unknown, fallback: string): string {
  return fleetErrorText(failure, fallback) || fallback
}

/**
 * Whether each run's ⋮ menu may hand it back, read from the fleet when the menu opens and reused for a short while,
 * and the hand-back itself. A refused hand-back keeps the fleet's own reason in `error` and is re-thrown.
 */
export const useClaudeFleetHandBackStore = defineStore('adx-claude-fleet-hand-back', () => {
  const fleetService = useClaudeFleetService()

  const checks = ref<Record<string, { at: number; check: FleetHandBackCheck }>>({})
  /** Run ids with a hand-back on its way. */
  const pending = ref<Record<string, boolean>>({})
  /** Bumped by each hand-back, so a read that started before it cannot bring back the answer it made stale. */
  const handBacks: Record<string, number> = {}
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const hasError = computed(() => error.value !== null)

  function checkOf(runId: string): FleetHandBackCheck | undefined {
    return checks.value[runId]?.check
  }

  function setCheck(runId: string, check: FleetHandBackCheck) {
    checks.value = { ...checks.value, [runId]: { at: Date.now(), check } }
  }

  /** Reads the run's eligibility unless a fresh answer is already held. */
  async function check(runId: string, { force = false } = {}) {
    const held = checks.value[runId]
    if (!force && held && held.check.status !== 'failed' && Date.now() - held.at < FLEET_HAND_BACK_CHECK_TTL_MS) return
    if (!held || held.check.status !== 'ready') setCheck(runId, { status: 'loading' })
    const handBacksBefore = handBacks[runId] ?? 0
    let answer: FleetHandBackCheck
    try {
      answer = { status: 'ready', eligibility: await fleetService.getRunHandBack(runId) }
    } catch (e) {
      answer = { status: 'failed', why: refusalText(e, 'Could not check whether this run can be handed back') }
    }
    if ((handBacks[runId] ?? 0) === handBacksBefore) setCheck(runId, answer)
  }

  async function handBack(runId: string, mode: FleetHandBackMode): Promise<FleetRunHandBackResult> {
    pending.value = { ...pending.value, [runId]: true }
    isLoading.value = true
    error.value = null
    try {
      return await fleetService.handBackRun(runId, mode)
    } catch (e) {
      error.value = refusalText(e, 'The run was not handed back')
      throw e
    } finally {
      const { [runId]: _done, ...still } = pending.value
      pending.value = still
      isLoading.value = Object.keys(still).length > 0
      handBacks[runId] = (handBacks[runId] ?? 0) + 1
      const { [runId]: _stale, ...kept } = checks.value
      checks.value = kept
    }
  }

  function clearError() {
    error.value = null
  }

  function reset() {
    checks.value = {}
    pending.value = {}
    isLoading.value = false
    error.value = null
  }

  return { checks, pending, isLoading, error, hasError, checkOf, check, handBack, clearError, reset }
})
